import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { StoreProvider, useStore } from "./context/StoreContext";
import { AdminProvider, useAdmin } from "./context/AdminContext";
import { UserAuthProvider, useUserAuth } from "./context/UserAuthContext";
import { CartProvider } from "./context/CartContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import { DemoModeProvider } from "./context/DemoModeContext";
import DemoBanner from "./components/demo/DemoBanner";
import DemoRouteGate from "./components/demo/DemoRouteGate";
import {
  DemoGateAccount,
  DemoGateConnexion,
  DemoGateForgotPassword,
  DemoGateInscription,
  DemoGatePayment,
  DemoGateResetPassword,
} from "./components/demo/DemoGates";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { BackToTop } from "./components/BackToTop";
import WelcomePopup from "./components/WelcomePopup";
import HomePage from "./pages/HomePage";
import ServicePage from "./pages/ServicePage";
import ConnexionPage from "./pages/ConnexionPage";
import InscriptionPage from "./pages/InscriptionPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import "./App.css";
import { SITE_SHELL_MAX } from "./constants/layout";

function LazyFallback() {
  return <div className="min-h-[200px]" />;
}

const ShowroomPage = lazy(() => import("./pages/ShowroomPage"));
const ShowroomUniversePage = lazy(() => import("./components/showroom/ShowroomUniversePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AgencesPage = lazy(() => import("./pages/AgencesPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PromosPage = lazy(() => import("./pages/PromosPage"));
// PromoDetailPage supprimé — les campagnes CMS redirigent vers /promos
const EvenementsPage = lazy(() => import("./pages/EvenementsPage"));
const CataloguePage = lazy(() => import("./pages/CataloguePage"));
const CatalogueCategoryPage = lazy(() => import("./pages/CatalogueCategoryPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const PanierPage = lazy(() => import("./pages/PanierPage"));
const OrderConfirmationPage = lazy(() => import("./pages/OrderConfirmationPage"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));

const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ShowroomPhotos = lazy(() => import("./pages/admin/ShowroomPhotos"));
const Events = lazy(() => import("./pages/admin/Events"));
const Promotions = lazy(() => import("./pages/admin/Promotions"));
const Brands = lazy(() => import("./pages/admin/Brands"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const Popup = lazy(() => import("./pages/admin/Popup"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductDetail = lazy(() => import("./pages/admin/AdminProductDetail"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminImportProducts = lazy(() => import("./pages/admin/AdminImportProducts"));
const AdminStockImport = lazy(() => import("./pages/admin/AdminStockImport"));
const AdminStores = lazy(() => import("./pages/admin/AdminStores"));
const AdminStock = lazy(() => import("./pages/admin/AdminStock"));
const AdminStockPilotage = lazy(() => import("./pages/admin/AdminStockPilotage"));
const AdminAutomation = lazy(() => import("./pages/admin/AdminAutomation"));
const AdminPricing = lazy(() => import("./pages/admin/AdminPricing"));
const AdminProAccounts = lazy(() => import("./pages/admin/AdminProAccounts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminQuotes = lazy(() => import("./pages/admin/AdminQuotes"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminCustomerDetail = lazy(() => import("./pages/admin/AdminCustomerDetail"));
const AdminCRM = lazy(() => import("./pages/admin/AdminCRM"));
const VendorDashboard = lazy(() => import("./pages/admin/VendorDashboard"));
const AdminPreparation = lazy(() => import("./pages/admin/AdminPreparation"));
const AdminManualOrder = lazy(() => import("./pages/admin/AdminManualOrder"));
const AdminManualQuote = lazy(() => import("./pages/admin/AdminManualQuote"));
const AdminStockAlerts = lazy(() => import("./pages/admin/AdminStockAlerts"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminReturns = lazy(() => import("./pages/admin/AdminReturns"));
const AdminCredits = lazy(() => import("./pages/admin/AdminCredits"));
const AdminStockMovements = lazy(() => import("./pages/admin/AdminStockMovements"));
const AdminTransfers = lazy(() => import("./pages/admin/AdminTransfers"));
const AdminStaff = lazy(() => import("./pages/admin/AdminStaff"));
const AdminPricingAdvanced = lazy(() => import("./pages/admin/AdminPricingAdvanced"));
const AdminPricingRules = lazy(() => import("./pages/admin/AdminPricingRules"));
const AdminForbidden = lazy(() => import("./pages/admin/AdminForbidden"));
const AdminProfile = lazy(() => import("./pages/admin/AdminProfile"));
const AdminStaffLogs = lazy(() => import("./pages/admin/AdminStaffLogs"));
const AdminAccounting = lazy(() => import("./pages/admin/AdminAccounting"));
const AdminPOS = lazy(() => import("./pages/admin/AdminPOS"));
const ExecutiveDashboard = lazy(() => import("./pages/admin/ExecutiveDashboard"));
const AdminSuppliers = lazy(() => import("./pages/admin/AdminSuppliers"));
const AdminPurchaseOrders = lazy(() => import("./pages/admin/AdminPurchaseOrders"));
const AdminReceipts = lazy(() => import("./pages/admin/AdminReceipts"));
const AdminReplenishment = lazy(() => import("./pages/admin/AdminReplenishment"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const AdminSetupWizard = lazy(() => import("./pages/admin/AdminSetupWizard"));
const DevTrackingPage = lazy(() => import("./pages/DevTrackingPage"));

const MentionsLegalesPage = lazy(() => import("./pages/legal/MentionsLegalesPage"));
const PolitiqueConfidentialitePage = lazy(() => import("./pages/legal/PolitiqueConfidentialitePage"));
const PolitiqueCookiesPage = lazy(() => import("./pages/legal/PolitiqueCookiesPage"));
const CgvPage = lazy(() => import("./pages/legal/CgvPage"));
const CguPage = lazy(() => import("./pages/legal/CguPage"));
const DemoAccessPage = lazy(() => import("./pages/DemoAccessPage"));

const AccountLayout = lazy(() => import("./pages/account/AccountLayout"));
const AccountDashboard = lazy(() => import("./pages/account/AccountDashboard"));
const AccountProfile = lazy(() => import("./pages/account/AccountProfile"));
const AccountAddresses = lazy(() => import("./pages/account/AccountAddresses"));
const AccountSecurity = lazy(() => import("./pages/account/AccountSecurity"));
const AccountOrders = lazy(() => import("./pages/account/AccountOrders"));
const AccountOrderDetail = lazy(() => import("./pages/account/AccountOrderDetail"));
const AccountQuotes = lazy(() => import("./pages/account/AccountQuotes"));
const AccountQuoteDetail = lazy(() => import("./pages/account/AccountQuoteDetail"));
const AccountDocuments = lazy(() => import("./pages/account/AccountDocuments"));
const AccountFavorites = lazy(() => import("./pages/account/AccountFavorites"));
const AccountQuickOrder = lazy(() => import("./pages/account/AccountQuickOrder"));
const AccountStore = lazy(() => import("./pages/account/AccountStore"));
const AccountNotifications = lazy(() => import("./pages/account/AccountNotifications"));

function AdminDefaultRedirect() {
  const { role } = useAdmin();
  if (role === "super_admin" || role === "admin") {
    return <Navigate to="home" replace />;
  }
  return <Navigate to="vendor" replace />;
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isDevTools = location.pathname.startsWith("/dev");
  const isShowroomHome =
    location.pathname === "/showroom" ||
    location.pathname === "/showroom/" ||
    location.pathname.startsWith("/showroom/");
  /* --epp-shell-top : bandeau démo (si session) + hauteur mesurée du header (voir Header.js) */
  const mainPadding =
    "pt-[calc(var(--epp-shell-top,220px)+env(safe-area-inset-top,0px))] min-h-screen w-full mx-auto";
  let mainShellClass = "";
  if (!isAdmin && !isDevTools) {
    mainShellClass = `${mainPadding} ${SITE_SHELL_MAX}${isShowroomHome ? " bg-black" : ""}`;
  }

  return (
    <>
      {!isAdmin && !isDevTools && (
        <>
          <DemoBanner />
          <Header />
        </>
      )}
      <main className={mainShellClass}>
      <DemoRouteGate>
      <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/demo" element={<DemoAccessPage />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />}>
          <Route index element={<AdminDefaultRedirect />} />
          <Route path="home" element={<AdminHome />} />
          <Route path="showroom" element={<ShowroomPhotos />} />
          <Route path="events" element={<Events />} />
          <Route path="promotions" element={<Promotions />} />
          <Route path="brands" element={<Brands />} />
          <Route path="settings" element={<Settings />} />
          <Route path="popup" element={<Popup />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/:productId" element={<AdminProductDetail />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="import-products" element={<AdminImportProducts />} />
          <Route path="import-stock" element={<AdminStockImport />} />
          <Route path="stores" element={<AdminStores />} />
          <Route path="stock" element={<AdminStock />} />
          <Route path="stock-pilotage" element={<AdminStockPilotage />} />
          <Route path="automation" element={<AdminAutomation />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="pro-accounts" element={<AdminProAccounts />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="quotes" element={<AdminQuotes />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="customers/:userId" element={<AdminCustomerDetail />} />
          <Route path="crm" element={<AdminCRM />} />
          <Route path="vendor" element={<VendorDashboard />} />
          <Route path="preparation" element={<AdminPreparation />} />
          <Route path="manual-order" element={<AdminManualOrder />} />
          <Route path="manual-quote" element={<AdminManualQuote />} />
          <Route path="stock-alerts" element={<AdminStockAlerts />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="returns" element={<AdminReturns />} />
          <Route path="credits" element={<AdminCredits />} />
          <Route path="stock-movements" element={<AdminStockMovements />} />
          <Route path="transfers" element={<AdminTransfers />} />
          <Route path="staff" element={<AdminStaff />} />
          <Route path="pricing-advanced" element={<AdminPricingAdvanced />} />
          <Route path="pricing-rules" element={<AdminPricingRules />} />
          <Route path="forbidden" element={<AdminForbidden />} />
          <Route path="accounting" element={<AdminAccounting />} />
          <Route path="pos" element={<AdminPOS />} />
          <Route path="executive" element={<ExecutiveDashboard />} />
          <Route path="suppliers" element={<AdminSuppliers />} />
          <Route path="purchase-orders" element={<AdminPurchaseOrders />} />
          <Route path="receipts" element={<AdminReceipts />} />
          <Route path="replenishment" element={<AdminReplenishment />} />
          <Route path="profile" element={<AdminProfile />} />
          <Route path="staff-logs" element={<AdminStaffLogs />} />
          <Route path="setup" element={<AdminSetupWizard />} />
        </Route>
        <Route path="/dev" element={<DevTrackingPage />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/plomberie" element={<ServicePage />} />
        <Route path="/chauffage" element={<ServicePage />} />
        <Route path="/climatisation" element={<ServicePage />} />
        <Route path="/piscines" element={<ServicePage />} />
        <Route path="/salle-de-bains" element={<ServicePage />} />
        <Route path="/showroom" element={<ShowroomPage />} />
        <Route path="/showroom/:slug" element={<ShowroomUniversePage />} />
        <Route path="/catalogue" element={<CataloguePage />} />
        <Route path="/catalogue/:slug1" element={<CatalogueCategoryPage />} />
        <Route path="/catalogue/:slug1/:slug2" element={<CatalogueCategoryPage />} />
        <Route path="/catalogue/:slug1/:slug2/:slug3" element={<CatalogueCategoryPage />} />
        <Route path="/categorie/:slug" element={<CategoryPage />} />
        <Route path="/produit/:slug" element={<ProductPage />} />
        <Route path="/promos/:index" element={<Navigate to="/promos" replace />} />
        <Route path="/promos" element={<PromosPage />} />
        <Route path="/evenements" element={<EvenementsPage />} />
        <Route path="/evenements/:index" element={<Navigate to="/evenements" replace />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
        <Route path="/politique-cookies" element={<PolitiqueCookiesPage />} />
        <Route path="/cgv" element={<CgvPage />} />
        <Route path="/cgu" element={<CguPage />} />
        <Route path="/agences" element={<AgencesPage />} />
        <Route path="/partenaires" element={<PartnersPage />} />
        <Route
          path="/connexion"
          element={
            <DemoGateConnexion>
              <ConnexionPage />
            </DemoGateConnexion>
          }
        />
        <Route
          path="/inscription"
          element={
            <DemoGateInscription>
              <InscriptionPage />
            </DemoGateInscription>
          }
        />
        <Route
          path="/mot-de-passe-oubli"
          element={
            <DemoGateForgotPassword>
              <ForgotPasswordPage />
            </DemoGateForgotPassword>
          }
        />
        <Route
          path="/reinitialiser-mot-de-passe"
          element={
            <DemoGateResetPassword>
              <ResetPasswordPage />
            </DemoGateResetPassword>
          }
        />
        {/* Espace client */}
        <Route
          path="/compte"
          element={
            <DemoGateAccount>
              <AccountLayout />
            </DemoGateAccount>
          }
        >
          <Route index element={<AccountDashboard />} />
          <Route path="profil" element={<AccountProfile />} />
          <Route path="adresses" element={<AccountAddresses />} />
          <Route path="securite" element={<AccountSecurity />} />
          <Route path="commandes" element={<AccountOrders />} />
          <Route path="commandes/:id" element={<AccountOrderDetail />} />
          <Route path="devis" element={<AccountQuotes />} />
          <Route path="devis/:id" element={<AccountQuoteDetail />} />
          <Route path="documents" element={<AccountDocuments />} />
          <Route path="favoris" element={<AccountFavorites />} />
          <Route path="commande-rapide" element={<AccountQuickOrder />} />
          <Route path="magasin-favori" element={<AccountStore />} />
          <Route path="notifications" element={<AccountNotifications />} />
        </Route>
        {/* Redirections anciennes routes */}
        <Route path="/commandes" element={<Navigate to="/compte/commandes" replace />} />
        <Route path="/devis" element={<Navigate to="/compte/devis" replace />} />
        <Route path="/devis/:quoteId" element={<Navigate to="/compte/devis" replace />} />
        <Route path="/favoris" element={<Navigate to="/compte/favoris" replace />} />
        <Route path="/commande-rapide" element={<Navigate to="/compte/commande-rapide" replace />} />
        <Route path="/documents" element={<Navigate to="/compte/documents" replace />} />
        <Route path="/panier" element={<PanierPage />} />
        <Route path="/commande/confirmation/:orderId" element={<OrderConfirmationPage />} />
        <Route
          path="/paiement"
          element={
            <DemoGatePayment>
              <PaymentPage />
            </DemoGatePayment>
          }
        />
        <Route
          path="/paiement/:orderId"
          element={
            <DemoGatePayment>
              <PaymentPage />
            </DemoGatePayment>
          }
        />
      </Routes>
      </Suspense>
      </DemoRouteGate>
      {!isAdmin && <Footer />}
      </main>
      {!isAdmin && <WelcomePopup />}
      <BackToTop />
    </>
  );
}

function StoreSyncer() {
  const { user, isAuthenticated } = useUserAuth();
  const { setSelectedId, selectedId } = useStore();

  useEffect(() => {
    if (isAuthenticated && user?.favorite_store_id && !selectedId) {
      setSelectedId(user.favorite_store_id);
    }
  }, [isAuthenticated, user?.favorite_store_id, selectedId, setSelectedId]);

  useEffect(() => {
    if (isAuthenticated && user?.favorite_store_id && user.favorite_store_id !== selectedId) {
      setSelectedId(user.favorite_store_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.favorite_store_id]);

  return null;
}

function App() {
  return (
    <StoreProvider>
      <AdminProvider>
        <UserAuthProvider>
        <CartProvider>
        <StoreSyncer />
        <Router>
          <DemoModeProvider>
            <CookieConsentProvider>
              <div className="App">
                <Toaster position="top-right" richColors />
                <AppRoutes />
                <CookieConsentBanner />
              </div>
            </CookieConsentProvider>
          </DemoModeProvider>
        </Router>
        </CartProvider>
        </UserAuthProvider>
      </AdminProvider>
    </StoreProvider>
  );
}

export default App;
